import React, { useState, useEffect } from "react"
import { graphql } from "gatsby"
import Layout from "../components/layout"
import styled, { css } from "styled-components"
import Series from "../components/series"
import ReactMarkdown from "react-markdown"
import Button from "../components/button"
import Hero from "../components/hero"

const Header = styled.header`
    object-fit: cover;
    overflow: hidden;
    position: relative;
    box-shadow: 0 1px 1px 0 rgba(66, 66, 66, 0.08),
        0 1px 3px 1px rgba(66, 66, 66, 0.36);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 1rem;
    z-index: 2;
    transition: height 0.5s;
    min-height: 400px;
    height: 45vh;
    max-height: 25rem;
    border-bottom: 3px solid ${props => props.theme.primary};
    margin-bottom: 0;
    @media screen and (max-width: 768px) {
        flex-direction: column;
        justify-content: flex-start;
        height: 28rem;
    }
`

const HeadlineContainer = styled.div`
    background-color: #ededed;
    width: 45%;
    height: calc(100% + 3rem);
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: flex-start;
    justify-content: center;
    bottom: 0;
    padding: 1rem 1rem 0.5rem;
    box-sizing: border-box;
    h1 {
        margin-left: -0.2rem;
    }
    div,
    h1 {
        z-index: 4;
    }
    &:after {
        content: "";
        position: absolute;
        background: #ededed;
        height: 200%;
        width: 10rem;
        left: -5rem;
        transform: rotate(10deg);
        z-index: 2;
        padding: 1px;
    }
    &:before {
        content: "";
        position: absolute;
        background: ${props => props.theme.primary};
        height: 200%;
        width: 5rem;
        left: -5rem;
        transform: rotate(6deg);
        z-index: 1;
    }
    @media screen and (max-width: 768px) {
        width: 100%;
        height: auto;
        padding-left: 1.75rem;
        h1 {
            color: white;
            margin-top: -3.1rem;
            margin-bottom: 1.7rem;
        }
        a {
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
        }
        &:before {
            display: none;
        }
        &:after {
            background: rgba(0, 0, 0, 0.7);
            transform: rotate(0deg);
            width: 100%;
            height: 3rem;
            left: 0;
            top: -3rem;
        }
    }
`

const HeroContainer = styled.div`
    position: relative;
    height: 100%;
    width: 55%;
    @media screen and (max-width: 768px) {
        width: 100%;
        height: 40rem;
    }
`

export default function LandingPage({ data }) {
    let defaultWidth

    if (typeof window !== `undefined`) {
        defaultWidth = window.innerWidth
    }
    const useWindowWidth = () => {
        const [width, setWidth] = useState(defaultWidth)

        useEffect(() => {
            const handler = () => {
                setWidth(window.innerWidth)
                setDesktop(window.innerWidth >= 768)
            }

            window.addEventListener(`resize`, handler)
            return () => window.removeEventListener(`resize`, handler)
        }, [])
        return width
    }
    const [desktop, setDesktop] = useState(useWindowWidth() >= 768)

    const page = data.markdownRemark.frontmatter
    const products = {}
    const allImages = data.allImageSharp.edges
    const images = {}
    const altImages = {}
    //This associates image links in markdown with gatsby-image's output in /static/
    //Access images like this: images[path]
    allImages.forEach(el => {
        images["/content/images/" + el.node.fluid.originalName] =
            el.node.fluid.srcSet
    })
    allImages.forEach(el => {
        altImages["/content/images/" + el.node.fluid.originalName] = el.node
    })

    return (
        <Layout>
            <Header>
                <HeroContainer>
                    <Hero
                        data={page.images}
                        images={images}
                        altImages={altImages}
                    />
                </HeroContainer>
                <HeadlineContainer>
                    <h1>{page.title}</h1>
                    <ReactMarkdown
                        source={page.features}
                        className="bullet-list bullet-list-black"
                    />
                    <Button header to="https://www.tuffshed.com/products/#/all">
                        Learn More
                    </Button>
                </HeadlineContainer>
            </Header>
            <main>
                {page.series.map((el, key) => {
                    return (
                        <Series
                            data={el}
                            key={key}
                            products={products}
                            images={images}
                            desktop={desktop}
                        />
                    )
                })}
            </main>
        </Layout>
    )
}
//TODO: Make import filters dynamic
export const query = graphql`
    query($slug: String!) {
        markdownRemark(frontmatter: { slug: { eq: $slug } }) {
            frontmatter {
                title
                features
                images
                slug
                series {
                    description
                    image
                    name
                    products {
                        description
                        features
                        linkText
                        name
                        images
                        url
                    }
                }
            }
        }
        allImageSharp {
            edges {
                node {
                    fixed {
                        aspectRatio
                        src
                        srcSet
                        originalName
                    }
                    fluid {
                        src
                        srcSet
                        originalImg
                        originalName
                    }
                }
            }
        }
    }
`
